import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { formatarDataBR, hojeISO } from '../common/data';

/**
 * Vistorias da unidade — a autovistoria da equipe e a visita do fiscal.
 *
 * O roteiro que vem junto ao criar a vistoria é um PONTO DE PARTIDA, não um
 * formulário fechado: exigência de vigilância sanitária muda por município, e
 * lista fixa deixaria de fora justamente o que a creche precisa checar. Por
 * isso todo item pode ser reescrito, removido, e novos podem ser acrescentados.
 *
 * A conformidade é calculada no backend sobre o que foi EFETIVAMENTE
 * verificado — item "não aplicável" (creche sem cozinha própria) e item que
 * ninguém olhou ficam de fora da conta, para uma vistoria de três itens não se
 * passar por vistoria completa.
 */

interface ResumoVistoria {
  total: number; conformes: number; naoConformes: number;
  naoAplicaveis: number; naoVerificados: number;
  conformidadePct: number | null; pendencias: number; pendenciasVencidas: number; concluida: boolean;
}

type Resultado = 'conforme' | 'nao_conforme' | 'nao_aplicavel' | 'nao_verificado';

interface ItemVistoria {
  id: number; categoria: string; descricao: string; resultado: Resultado;
  observacao: string | null; prazo_correcao: string | null; corrigido_em: string | null; ordem: number;
}

interface Vistoria {
  id: number; data: string; orgao: string | null; responsavel: string | null;
  tipo: 'interna' | 'oficial'; observacoes: string | null;
  resumo: ResumoVistoria; itens?: ItemVistoria[];
}

const card: React.CSSProperties = {
  background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginBottom: 20,
};

const RESULTADOS: Array<{ valor: Resultado; label: string; cor: string }> = [
  { valor: 'conforme', label: 'Conforme', cor: '#2e7d32' },
  { valor: 'nao_conforme', label: 'Não conforme', cor: '#c62828' },
  { valor: 'nao_aplicavel', label: 'Não se aplica', cor: '#616161' },
  { valor: 'nao_verificado', label: 'Não verificado', cor: '#9e9e9e' },
];

const corDoResultado = (r: Resultado) => RESULTADOS.find((x) => x.valor === r)?.cor || '#616161';

/** Vencida = prazo passou e ninguém registrou a correção. Compara texto ISO — sem `Date`, sem fuso. */
function prazoVencido(item: ItemVistoria, hoje: string): boolean {
  return item.resultado === 'nao_conforme'
    && !item.corrigido_em
    && Boolean(item.prazo_correcao)
    && String(item.prazo_correcao).slice(0, 10) < hoje;
}

export default function VistoriasSection({ aoMudar }: { aoMudar?: () => void }) {
  const [vistorias, setVistorias] = useState<Vistoria[]>([]);
  const [aberta, setAberta] = useState<Vistoria | null>(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [criando, setCriando] = useState(false);
  const [nova, setNova] = useState({ data: hojeISO(), tipo: 'interna', orgao: '', responsavel: '' });
  const [novoItem, setNovoItem] = useState({ categoria: '', descricao: '' });
  const hoje = hojeISO();

  const mostrarErro = (e: any, padrao: string) => setErro(e?.response?.data?.message || padrao);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get<Vistoria[]>('/administrativo/vistorias');
      setVistorias(data);
      setErro('');
    } catch (e: any) {
      mostrarErro(e, 'Não foi possível carregar as vistorias.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const abrir = async (id: number) => {
    try {
      const { data } = await api.get<Vistoria>(`/administrativo/vistorias/${id}`);
      setAberta(data);
      setErro('');
    } catch (e: any) {
      mostrarErro(e, 'Não foi possível abrir a vistoria.');
    }
  };

  const criar = async () => {
    if (!nova.data) { setErro('Informe a data da vistoria.'); return; }
    setSalvando(true);
    try {
      const { data } = await api.post<Vistoria>('/administrativo/vistorias', nova);
      setAberta(data);
      setCriando(false);
      setNova({ data: hojeISO(), tipo: 'interna', orgao: '', responsavel: '' });
      await carregar();
      aoMudar?.();
      setErro('');
    } catch (e: any) {
      mostrarErro(e, 'Não foi possível criar a vistoria.');
    } finally {
      setSalvando(false);
    }
  };

  const remover = async (id: number) => {
    if (!window.confirm('Excluir esta vistoria e todos os seus itens?')) return;
    try {
      await api.delete(`/administrativo/vistorias/${id}`);
      if (aberta?.id === id) setAberta(null);
      await carregar();
      aoMudar?.();
    } catch (e: any) {
      mostrarErro(e, 'Não foi possível excluir a vistoria.');
    }
  };

  /**
   * Salva o item e recarrega a vistoria inteira.
   *
   * Recarregar em vez de remendar o estado local porque o resumo (conformidade,
   * pendências) é calculado no backend: atualizar só o item deixaria o
   * percentual no topo mentindo até o próximo F5.
   */
  const salvarItem = async (idItem: number, mudanca: Partial<ItemVistoria>) => {
    if (!aberta) return;
    setSalvando(true);
    try {
      await api.put(`/administrativo/vistorias/${aberta.id}/itens/${idItem}`, mudanca);
      await abrir(aberta.id);
      await carregar();
      aoMudar?.();
    } catch (e: any) {
      mostrarErro(e, 'Não foi possível salvar o item.');
    } finally {
      setSalvando(false);
    }
  };

  const adicionarItem = async () => {
    if (!aberta) return;
    if (!novoItem.descricao.trim()) { setErro('Descreva o que deve ser verificado.'); return; }
    setSalvando(true);
    try {
      await api.post(`/administrativo/vistorias/${aberta.id}/itens`, novoItem);
      setNovoItem({ categoria: '', descricao: '' });
      await abrir(aberta.id);
      await carregar();
      setErro('');
    } catch (e: any) {
      mostrarErro(e, 'Não foi possível adicionar o item.');
    } finally {
      setSalvando(false);
    }
  };

  const removerItem = async (idItem: number) => {
    if (!aberta) return;
    if (!window.confirm('Remover este item do roteiro desta vistoria?')) return;
    try {
      await api.delete(`/administrativo/vistorias/${aberta.id}/itens/${idItem}`);
      await abrir(aberta.id);
      await carregar();
    } catch (e: any) {
      mostrarErro(e, 'Não foi possível remover o item.');
    }
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>🔍 Vistorias</h3>
        <button type="button" onClick={() => { setCriando((v) => !v); setErro(''); }}>
          {criando ? 'Cancelar' : 'Nova vistoria'}
        </button>
      </div>

      {erro && <p style={{ color: '#c62828' }}>{erro}</p>}

      {criando && (
        <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <div className="linha-campos linha-2">
            <label>Data<input type="date" value={nova.data} onChange={(e) => setNova({ ...nova, data: e.target.value })} /></label>
            <label>Tipo
              <select value={nova.tipo} onChange={(e) => setNova({ ...nova, tipo: e.target.value })}>
                <option value="interna">Autovistoria da equipe</option>
                <option value="oficial">Visita oficial do órgão</option>
              </select>
            </label>
          </div>
          <div className="linha-campos linha-2">
            <label>Órgão (se oficial)<input value={nova.orgao} onChange={(e) => setNova({ ...nova, orgao: e.target.value })} placeholder="Vigilância Sanitária, Bombeiros…" /></label>
            <label>Responsável<input value={nova.responsavel} onChange={(e) => setNova({ ...nova, responsavel: e.target.value })} /></label>
          </div>
          {/* Sem número fixo de pontos no texto: o roteiro vive no backend e
              mudaria aqui sem ninguém lembrar de atualizar a frase. */}
          <p style={{ fontSize: 12, color: '#666', margin: '8px 0' }}>
            A vistoria já vem com um roteiro sugerido. Você pode editar, remover ou acrescentar itens depois.
          </p>
          <button type="button" onClick={criar} disabled={salvando}>
            {salvando ? 'Criando…' : 'Criar com o roteiro sugerido'}
          </button>
        </div>
      )}

      {carregando ? <p>Carregando…</p> : vistorias.length === 0 ? (
        <p style={{ color: '#666' }}>Nenhuma vistoria registrada ainda.</p>
      ) : (
        <table className="tabela" style={{ width: '100%' }}>
          <thead>
            <tr><th>Data</th><th>Tipo</th><th>Conformidade</th><th>Pendências</th><th></th></tr>
          </thead>
          <tbody>
            {vistorias.map((v) => (
              <tr key={v.id}>
                <td>{formatarDataBR(v.data)}</td>
                <td>{v.tipo === 'oficial' ? `Oficial${v.orgao ? ` — ${v.orgao}` : ''}` : 'Interna'}</td>
                <td>
                  {v.resumo.conformidadePct === null
                    ? <span style={{ color: '#9e9e9e' }}>não medida</span>
                    : `${v.resumo.conformidadePct}%`}
                  {!v.resumo.concluida && v.resumo.naoVerificados > 0 && (
                    <span style={{ color: '#9e9e9e', fontSize: 12 }}> ({v.resumo.naoVerificados} sem verificar)</span>
                  )}
                </td>
                <td>
                  {v.resumo.pendenciasVencidas > 0 && (
                    <strong style={{ color: '#c62828' }}>{v.resumo.pendenciasVencidas} vencida(s) </strong>
                  )}
                  {v.resumo.pendencias === 0 ? '—' : `${v.resumo.pendencias} em aberto`}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button type="button" onClick={() => abrir(v.id)}>Abrir</button>{' '}
                  <button type="button" onClick={() => remover(v.id)}>Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {aberta && (
        <div style={{ marginTop: 20, borderTop: '2px solid #eee', paddingTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <h4 style={{ margin: 0 }}>
              Vistoria de {formatarDataBR(aberta.data)}
              {aberta.responsavel ? ` — ${aberta.responsavel}` : ''}
            </h4>
            <button type="button" onClick={() => setAberta(null)}>Fechar</button>
          </div>
          <p style={{ fontSize: 13, color: '#555' }}>
            {aberta.resumo.conformes} conforme(s) · {aberta.resumo.naoConformes} não conforme(s) ·{' '}
            {aberta.resumo.naoAplicaveis} não se aplica · {aberta.resumo.naoVerificados} sem verificar
          </p>

          <table className="tabela" style={{ width: '100%' }}>
            <thead>
              <tr><th style={{ width: '32%' }}>Item</th><th>Resultado</th><th>Observação</th><th>Prazo</th><th>Corrigido</th><th></th></tr>
            </thead>
            <tbody>
              {(aberta.itens || []).map((i) => (
                <tr key={i.id} style={prazoVencido(i, hoje) ? { background: '#fff3f3' } : undefined}>
                  <td>
                    <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase' }}>{i.categoria}</div>
                    <input
                      defaultValue={i.descricao}
                      onBlur={(e) => e.target.value !== i.descricao && salvarItem(i.id, { descricao: e.target.value })}
                      style={{ width: '100%' }}
                    />
                  </td>
                  <td>
                    <select
                      value={i.resultado}
                      onChange={(e) => salvarItem(i.id, { resultado: e.target.value as Resultado })}
                      style={{ color: corDoResultado(i.resultado), fontWeight: 600 }}
                    >
                      {RESULTADOS.map((r) => <option key={r.valor} value={r.valor}>{r.label}</option>)}
                    </select>
                  </td>
                  <td>
                    <input
                      defaultValue={i.observacao || ''}
                      onBlur={(e) => e.target.value !== (i.observacao || '') && salvarItem(i.id, { observacao: e.target.value })}
                      style={{ width: '100%' }}
                    />
                  </td>
                  {/* Prazo e correção só fazem sentido para não conformidade — o
                      backend limpa os dois quando o resultado deixa de ser esse. */}
                  <td>
                    {i.resultado === 'nao_conforme' ? (
                      <input
                        type="date"
                        value={i.prazo_correcao?.slice(0, 10) || ''}
                        onChange={(e) => salvarItem(i.id, { prazo_correcao: e.target.value || null })}
                      />
                    ) : <span style={{ color: '#bbb' }}>—</span>}
                    {prazoVencido(i, hoje) && (
                      <div style={{ color: '#c62828', fontSize: 11, fontWeight: 600 }}>prazo vencido</div>
                    )}
                  </td>
                  <td>
                    {i.resultado === 'nao_conforme' ? (
                      <input
                        type="date"
                        value={i.corrigido_em?.slice(0, 10) || ''}
                        onChange={(e) => salvarItem(i.id, { corrigido_em: e.target.value || null })}
                      />
                    ) : <span style={{ color: '#bbb' }}>—</span>}
                  </td>
                  <td><button type="button" onClick={() => removerItem(i.id)}>Remover</button></td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ flex: '0 0 200px' }}>Categoria
              <input value={novoItem.categoria} onChange={(e) => setNovoItem({ ...novoItem, categoria: e.target.value })} placeholder="Geral" />
            </label>
            <label style={{ flex: 1, minWidth: 260 }}>Novo ponto a verificar
              <input value={novoItem.descricao} onChange={(e) => setNovoItem({ ...novoItem, descricao: e.target.value })} />
            </label>
            <button type="button" onClick={adicionarItem} disabled={salvando}>Acrescentar</button>
          </div>
        </div>
      )}
    </div>
  );
}
